import { useState } from 'react';
import { Box, Button, Center, Loader, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { AuthCard } from 'src/auth/components/AuthCard';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type FormValues = {
  websiteUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  numberOfAdvisers: number | '';
  currentCrm: string;
};

export default function OnboardingPage() {
  const [submitted, setSubmitted] = useState(false);

  const { mutateAsync, isPending, error } = useMutation<null, Error, FormValues>({
    mutationFn: async values =>
      api
        .post<null>('public/onboarding/apply', {
          websiteUrl: values.websiteUrl,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          numberOfAdvisers: values.numberOfAdvisers === '' ? null : values.numberOfAdvisers,
          currentCrm: values.currentCrm.trim() || null,
        })
        .then(() => null),
  });

  const form = useForm<FormValues>({
    mode: 'uncontrolled',
    initialValues: {
      websiteUrl: '',
      firstName: '',
      lastName: '',
      email: '',
      numberOfAdvisers: '',
      currentCrm: '',
    },
    validate: {
      websiteUrl: v => (v.trim().length === 0 ? 'Website is required' : null),
      firstName: v => (v.trim().length === 0 ? 'First name is required' : null),
      lastName: v => (v.trim().length === 0 ? 'Last name is required' : null),
      email: v => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Valid email required'),
    },
  });

  const handleSubmit = form.onSubmit(async values => {
    await mutateAsync(values);
    setSubmitted(true);
  });

  if (submitted) {
    return (
      <AuthPageLayout>
        <AuthCard>
          <Stack gap="md" align="center">
            <Center w={64} h={64} bg="green.1" style={{ borderRadius: '50%' }}>
              <IconCheck size={32} color="var(--mantine-color-green-6)" />
            </Center>
            <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
              Application submitted
            </Text>
            <Text size="sm" c="gray.6" ta="center">
              We&apos;ll review your application and be in touch soon.
            </Text>
          </Stack>
        </AuthCard>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <AuthCard>
        <Stack gap="lg">
          <Stack gap={4}>
            <Text fz="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Get started
            </Text>
            <Text size="sm" c="gray.6">
              Fill in your details to apply for an account.
            </Text>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack gap="sm">
              <TextInput
                required
                label="Website"
                placeholder="e.g. https://example.com"
                key={form.key('websiteUrl')}
                {...form.getInputProps('websiteUrl')}
              />
              <TextInput
                required
                label="First name"
                placeholder="e.g. Jane"
                key={form.key('firstName')}
                {...form.getInputProps('firstName')}
              />
              <TextInput
                required
                label="Last name"
                placeholder="e.g. Smith"
                key={form.key('lastName')}
                {...form.getInputProps('lastName')}
              />
              <TextInput
                required
                label="Email address"
                placeholder="e.g. jane@example.com"
                key={form.key('email')}
                {...form.getInputProps('email')}
              />
              <NumberInput
                label="Number of advisers"
                placeholder="e.g. 5"
                min={1}
                key={form.key('numberOfAdvisers')}
                {...form.getInputProps('numberOfAdvisers')}
              />
              <TextInput
                label="Current CRM"
                placeholder="e.g. Salesforce"
                key={form.key('currentCrm')}
                {...form.getInputProps('currentCrm')}
              />

              {error && (
                <Text size="sm" c="red.6">
                  Something went wrong. Please try again.
                </Text>
              )}

              <Box mt="xs">
                <Button
                  fullWidth
                  size="xl"
                  type="submit"
                  disabled={isPending}
                  leftSection={isPending ? <Loader size="sm" color="gray.4" /> : undefined}
                >
                  Continue
                </Button>
              </Box>
            </Stack>
          </form>
        </Stack>
      </AuthCard>
    </AuthPageLayout>
  );
}
